import { idsFromRoute, type FeedbackContext } from "@edu/shared";
import { recentFails } from "./fails.ts";

/**
 * Снимок технической обстановки, который прицепляется к заметке (раздел 3.3
 * `docs/feedback-notes-design.md`).
 *
 * **Предмет разработки здесь именно снимок, а поле ввода это упаковка к нему.**
 * Виджет обязан выигрывать у мессенджера, а выигрывает он ровно двумя вещами:
 * заметка снимается в момент проблемы, и к тексту сама прицепляется техника,
 * которую человек не напишет никогда. Заметка «тут неточность» с `lessonId` и
 * версией сборки чинится, без них она мусор.
 *
 * ⚠️ **Снимок собирается в момент ОТКРЫТИЯ окна, а не в момент нажатия
 * «Отправить»**, и зовёт эту функцию поэтому провайдер, а не форма. Сценарий,
 * который это задаёт: у преподавателя отвалилось видео, он открывает окно и
 * пишет об этом. `LiveKitRoom` при разрыве зовёт `onDisconnected`, комната
 * уходит с маршрута, размонтируется и снимает поставщика. Собери мы снимок на
 * «Отправить», заметка про пропавшее видео приехала бы без `lessonId`, без
 * `role` и без доски, то есть без всего, ради чего фича существует.
 *
 * ⚠️ **Ни одного обязательного поля.** Снимок собирается из браузерных API,
 * половина которых где-нибудь отсутствует (`navigator.connection` вне Chromium,
 * доска вне комнаты). Приоритет обратный привычному: уезжает заметка, а снимок
 * это то, что удалось собрать. Поэтому здесь нет ни одного `throw` и ни одной
 * проверки, способной отменить отправку.
 *
 * ⚠️ **Скриншота здесь нет и не будет.** Он полезнее всего перечисленного, и он
 * же это рендер доски, то есть содержимое урока и ответы ученика в базе. Решение
 * окончательное, а не «пока не сделали».
 */

/**
 * Версия бандла. Впекается в сам бандл через `define` в `vite.config.ts`, а не
 * читается из `config.json`: в день, когда появится service worker (5.1
 * CLAUDE.md планирует `vite-plugin-pwa`), вкладка начнёт держать старый бандл
 * при свежем `config.json`, то есть поле показывало бы версию сервера и врало
 * бы ровно тогда, когда нужно.
 *
 * ⚠️ **Ссылка не голая, и это не суеверие.** `apps/web` гоняет тесты через
 * `node --test --experimental-strip-types`, который подстановок Vite не делает,
 * а `declare const` существует только для тайпчека. Голая ссылка уронила бы
 * первый же тест, импортирующий этот модуль, с `ReferenceError`.
 */
function buildId(): string | undefined {
  return typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : undefined;
}

/**
 * Форма `navigator.connection`. Своим объявлением, потому что в стандартных
 * типах DOM его нет: Network Information API поддерживает только Chromium.
 * Отсутствие полей это `null` («не знаем»), а не догадка.
 */
interface NetworkInformation {
  effectiveType?: unknown;
  downlink?: unknown;
}

/**
 * Собрать снимок.
 *
 * `extra` это слияние того, что зарегистрировали поставщики (комната и доска,
 * см. `feedback-context.tsx`). Кладётся ПОВЕРХ вычисленного здесь: комната знает
 * про себя больше, чем адрес. Идентификаторы при этом всё равно приезжают из
 * адреса, поэтому на архиве, где комнаты и поставщика нет вовсе, `lessonId`
 * есть.
 */
export function snapshot(pathname: string, extra: Partial<FeedbackContext>): FeedbackContext {
  const base: FeedbackContext = {
    build: buildId(),
    ...viewport(),
    ...network(),
    /**
     * ⚠️ Идентификаторы берутся разбором адреса, а НЕ через `useParams()`:
     * виджет монтируется рядом с набором маршрутов, а не внутри элемента
     * маршрута, и `useParams` вернул бы там пустой объект всегда и молча.
     * Заодно тем же разбором из адреса навсегда исключён сегмент `join`: за ним
     * стоит код приглашения, то есть секрет, а не идентификатор.
     */
    ...idsFromRoute(pathname),
    fails: recentFails(),
  };
  // Незаданные поля поставщиков не должны затирать вычисленное: комната,
  // отдавшая `{ role: undefined }`, не отменяет роль, она про неё не знает
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined) (base as Record<string, unknown>)[key] = value;
  }
  return base;
}

/**
 * Размер окна. Половина визуальных жалоб это раскладка на конкретной ширине, и
 * «съезжает панель» без трёх этих чисел не воспроизводится никогда.
 *
 * Округляем: `innerWidth` бывает дробным при зуме, а контракт требует целое, и
 * отказ схемы это `400`, то есть постоянно потерянная заметка.
 */
function viewport(): Pick<FeedbackContext, "viewport" | "online"> {
  if (typeof window === "undefined") return {};
  return {
    viewport: {
      w: Math.round(window.innerWidth),
      h: Math.round(window.innerHeight),
      dpr: window.devicePixelRatio,
    },
    // Различает «не отправилось» и «отправилось, но неправильно». Врёт нередко
    // (браузер считает сетью и подключение к вышке без доступа наружу), но
    // именно это враньё и полезно: оно совпадает с тем, что видел человек
    online: typeof navigator === "undefined" ? undefined : navigator.onLine,
  };
}

/** Что известно о канале. Только Chromium, в остальных браузерах ничего. */
function network(): Pick<FeedbackContext, "effectiveType" | "downlink"> {
  if (typeof navigator === "undefined") return {};
  const info = (navigator as { connection?: NetworkInformation }).connection;
  if (info === undefined) return {};
  return {
    effectiveType: typeof info.effectiveType === "string" ? info.effectiveType.slice(0, 16) : null,
    // Отрицательное или бесконечное значение отвергнет схема, а отказ схемы
    // это потерянная заметка, поэтому проверяем конечность, а не только тип
    downlink:
      typeof info.downlink === "number" && Number.isFinite(info.downlink) && info.downlink >= 0
        ? Math.min(info.downlink, 100_000)
        : null,
  };
}
